import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import DailyPerformanceTableBloomberg from './DailyPerformanceTableBloomberg';
import TWRPerformanceBloomberg from './TWRPerformanceBloomberg';
import { UnifiedChartsBloomberg } from './bloomberg';
import '../styles/bloomberg-theme.css';

interface ConsolidatedData {
  current_value: number;
  initial_value: number;
  total_return: number;
  total_return_percent: number;
  change_24h: number;
  change_24h_percent: number;
  change_7d: number;
  change_7d_percent: number;
  change_30d: number;
  change_30d_percent: number;
  breakdown: {
    wallet_balance_sol: number;
    wallet_balance_usdc: number;
    drift_balance_usd: number;
    orca_positions_value_usd: number;
  };
}

const ConsolidatedDashboardBloomberg: React.FC = () => {
  const { user } = useAuth();
  const [performanceData, setPerformanceData] = useState<ConsolidatedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPerformanceData = useCallback(async () => {
    try {
      setLoading(true);
      const perfData = await apiService.getConsolidatedPerformance();
      setPerformanceData(perfData);
      setError(null);
    } catch (err) {
      setError('FAILED TO LOAD CONSOLIDATED PERFORMANCE DATA');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.is_admin) {
      fetchPerformanceData();
      const interval = setInterval(fetchPerformanceData, 60000);
      return () => clearInterval(interval);
    }
  }, [fetchPerformanceData, user]);

  const formatCurrency = (value: number) => {
    const safeValue = isNaN(value) || !isFinite(value) ? 0 : value;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(safeValue);
  };

  const formatPercent = (value: number) => {
    const safeValue = isNaN(value) || !isFinite(value) ? 0 : value;
    return `${safeValue >= 0 ? '+' : ''}${safeValue.toFixed(2)}%`;
  };

  if (!user?.is_admin) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <span style={{ color: 'var(--bb-red)' }}>ADMIN ACCESS REQUIRED</span>
      </div>
    );
  }

  if (loading && !performanceData) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <span className="bb-blink" style={{ color: 'var(--bb-amber)' }}>LOADING CONSOLIDATED DATA...</span>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{
          color: 'var(--bb-amber)',
          fontSize: '18px',
          fontWeight: 'bold',
          letterSpacing: '0.1em',
          marginBottom: '5px'
        }}>
          CONSOLIDATED PORTFOLIO
        </h2>
        <p style={{ color: 'var(--bb-text-secondary)', fontSize: '11px' }}>
          AGGREGATED PERFORMANCE ACROSS ALL WALLETS
        </p>
      </div>

      {error && (
        <div style={{
          padding: '10px',
          border: '1px solid var(--bb-red)',
          backgroundColor: 'rgba(255, 0, 0, 0.1)',
          marginBottom: '20px'
        }}>
          <span style={{ color: 'var(--bb-red)' }}>ERROR: {error}</span>
        </div>
      )}

      {performanceData && (
        <>
          {/* Metrics Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '15px',
            marginBottom: '20px'
          }}>
            <div className="bb-panel">
              <div className="bb-panel-content" style={{ padding: '15px' }}>
                <div className="bb-data-label">TOTAL PORTFOLIO VALUE</div>
                <div className="bb-data-value" style={{ fontSize: '20px', marginTop: '8px' }}>
                  {formatCurrency(performanceData.current_value)}
                </div>
                <div className={performanceData.total_return >= 0 ? 'bb-value-positive' : 'bb-value-negative'}
                     style={{ fontSize: '14px', marginTop: '8px' }}>
                  {formatCurrency(performanceData.total_return)} ({formatPercent(performanceData.total_return_percent)})
                </div>
              </div>
            </div>

            <div className="bb-panel">
              <div className="bb-panel-content" style={{ padding: '15px' }}>
                <div className="bb-data-label">24H RETURN</div>
                <div className={performanceData.change_24h >= 0 ? 'bb-value-positive' : 'bb-value-negative'}
                     style={{ fontSize: '20px', marginTop: '8px' }}>
                  {formatCurrency(performanceData.change_24h)}
                </div>
                <div style={{ fontSize: '14px', marginTop: '8px' }}>
                  {formatPercent(performanceData.change_24h_percent)}
                </div>
              </div>
            </div>

            <div className="bb-panel">
              <div className="bb-panel-content" style={{ padding: '15px' }}>
                <div className="bb-data-label">7D RETURN</div>
                <div className={performanceData.change_7d >= 0 ? 'bb-value-positive' : 'bb-value-negative'}
                     style={{ fontSize: '20px', marginTop: '8px' }}>
                  {formatCurrency(performanceData.change_7d)}
                </div>
                <div style={{ fontSize: '14px', marginTop: '8px' }}>
                  {formatPercent(performanceData.change_7d_percent)}
                </div>
              </div>
            </div>

            <div className="bb-panel">
              <div className="bb-panel-content" style={{ padding: '15px' }}>
                <div className="bb-data-label">30D RETURN</div>
                <div className={performanceData.change_30d >= 0 ? 'bb-value-positive' : 'bb-value-negative'}
                     style={{ fontSize: '20px', marginTop: '8px' }}>
                  {formatCurrency(performanceData.change_30d)}
                </div>
                <div style={{ fontSize: '14px', marginTop: '8px' }}>
                  {formatPercent(performanceData.change_30d_percent)}
                </div>
              </div>
            </div>
          </div>

          {/* TWR Performance */}
          <div style={{ marginBottom: '20px' }}>
            <TWRPerformanceBloomberg isAdmin={true} />
          </div>

          {/* Unified Charts */}
          <UnifiedChartsBloomberg
            isConsolidated={true}
            initialPeriod="all"
          />

          {/* Portfolio Breakdown */}
          <div className="bb-panel" style={{ marginBottom: '20px' }}>
            <div className="bb-panel-header">AGGREGATED PORTFOLIO BREAKDOWN</div>
            <div className="bb-panel-content" style={{ padding: '15px' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '15px'
              }}>
                <div>
                  <div className="bb-data-label">TOTAL WALLET SOL</div>
                  <div className="bb-data-value">{performanceData.breakdown.wallet_balance_sol.toFixed(4)} SOL</div>
                </div>
                <div>
                  <div className="bb-data-label">TOTAL WALLET USDC</div>
                  <div className="bb-data-value">{formatCurrency(performanceData.breakdown.wallet_balance_usdc)}</div>
                </div>
                <div>
                  <div className="bb-data-label">TOTAL DRIFT BALANCE</div>
                  <div className="bb-data-value">{formatCurrency(performanceData.breakdown.drift_balance_usd)}</div>
                </div>
                <div>
                  <div className="bb-data-label">TOTAL ORCA POSITIONS</div>
                  <div className="bb-data-value">{formatCurrency(performanceData.breakdown.orca_positions_value_usd)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Daily Performance Table */}
          <DailyPerformanceTableBloomberg isConsolidated={true} />
        </>
      )}
    </div>
  );
};

export default ConsolidatedDashboardBloomberg;